import { AnimatePresence, motion } from "framer-motion";
import { LEADS } from "../../constants/testIds";
import { CONTACTED_SUB_STATUSES } from "../../lib/leadTabs";
import { relativeTimeFromNow } from "../../lib/format";
import { Button } from "../ui/button";
import { StatusPill } from "../common/StatusPill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

// UX-CRM-03 §1/§4: the Excel-style table every one of the 5 Lead tabs renders — one component,
// columns/row-actions adapt to `tabKey` rather than 4-5 near-duplicate table components. Clicking
// anywhere on a row (except its own action buttons) opens `LeadDetailDrawer` — see that file for
// the full field list a click reveals.
export function LeadTable({ tabKey, leads, planById, onRowClick, onSubStatusChange, onMarkContacted, onConvert, onMarkLost, onReopen, onViewMember }) {
  if (leads.length === 0) {
    return <div className="rounded-[14px] p-8 text-center text-sm" data-testid={LEADS.emptyState} style={{ background: "#fff", border: "1px solid #EBEAE5", color: "#68686E" }}>No leads here right now.</div>;
  }

  return (
    <div className="rounded-[14px] overflow-hidden overflow-x-auto" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
      <table className="w-full text-sm" data-testid={LEADS.table}>
        <thead>
          <tr className="text-left" style={{ borderBottom: "1px solid #EBEAE5" }}>
            <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "#A0A0A5" }}>Name</th>
            <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "#A0A0A5" }}>Phone</th>
            {(tabKey === "new_lead" || tabKey === "pipeline") && <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "#A0A0A5" }}>Plan interest</th>}
            {tabKey === "contacted" && <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "#A0A0A5" }}>Sub-status</th>}
            {tabKey === "contacted" && <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "#A0A0A5" }}>Next contact</th>}
            {tabKey === "contacted" && <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "#A0A0A5" }}>Comment</th>}
            <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "#A0A0A5" }}>{tabKey === "converted" ? "Converted" : tabKey === "lost" ? "Lost on" : "Captured"}</th>
            <th className="px-4 py-2.5"></th>
          </tr>
        </thead>
        <tbody>
          <AnimatePresence mode="popLayout">
            {leads.map((lead) => (
              <motion.tr
                key={lead.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                data-testid={LEADS.row(lead.id)} onClick={() => onRowClick(lead)}
                className="cursor-pointer hover:bg-black/[0.02] transition-colors" style={{ borderBottom: "1px solid #F3F2EE" }}
              >
                <td className="px-4 py-2.5 font-medium" style={{ color: "#141416" }}>{lead.contact.name}</td>
                <td className="px-4 py-2.5" style={{ color: "#68686E" }}>{lead.contact.phone}</td>

                {(tabKey === "new_lead" || tabKey === "pipeline") && (
                  <td className="px-4 py-2.5" style={{ color: "#68686E" }}>{lead.plan_interest && planById[lead.plan_interest] ? planById[lead.plan_interest].name : "—"}</td>
                )}

                {tabKey === "contacted" && (
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <Select value={lead.stage} onValueChange={(v) => onSubStatusChange(lead.id, v)}>
                      <SelectTrigger data-testid={LEADS.rowSubStatusSelect(lead.id)} className="h-7 text-xs w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>{CONTACTED_SUB_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                )}
                {tabKey === "contacted" && (
                  <td className="px-4 py-2.5 text-xs" style={{ color: "#68686E" }}>
                    {lead.follow_up_at ? (
                      <span className="inline-flex items-center gap-1.5">
                        {new Date(lead.follow_up_at).toLocaleString()}
                        {new Date(lead.follow_up_at).getTime() < Date.now() && (
                          <StatusPill status="overdue" label="Overdue" testId={LEADS.rowOverdueBadge(lead.id)} />
                        )}
                      </span>
                    ) : "—"}
                  </td>
                )}
                {tabKey === "contacted" && <td className="px-4 py-2.5 text-xs max-w-[180px] truncate" style={{ color: "#68686E" }}>{lead.note || "—"}</td>}

                <td className="px-4 py-2.5 text-xs" style={{ color: "#A0A0A5" }}>
                  {relativeTimeFromNow(tabKey === "converted" || tabKey === "lost" ? lead.updated_at : lead.created_at)}
                </td>

                <td className="px-4 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {(tabKey === "new_lead" || tabKey === "pipeline") && (
                    <Button size="sm" variant="outline" data-testid={LEADS.rowMarkContactedButton(lead.id)} onClick={() => onMarkContacted(lead.id)} className="h-7 text-xs mr-1.5">Mark contacted</Button>
                  )}
                  {(tabKey === "new_lead" || tabKey === "pipeline" || tabKey === "contacted") && (
                    <>
                      <Button size="sm" data-testid={LEADS.rowConvertButton(lead.id)} onClick={() => onConvert(lead)} className="h-7 text-xs text-white mr-2" style={{ background: "#D6202B" }}>Convert</Button>
                      <button data-testid={LEADS.rowMarkLostButton(lead.id)} onClick={() => onMarkLost(lead.id)} className="text-xs hover:underline" style={{ color: "#A0A0A5" }}>Lost</button>
                    </>
                  )}
                  {tabKey === "converted" && (
                    <Button size="sm" variant="outline" data-testid={LEADS.rowViewMemberButton(lead.id)} onClick={() => onViewMember(lead)} className="h-7 text-xs">View member</Button>
                  )}
                  {tabKey === "lost" && (
                    <Button size="sm" variant="outline" data-testid={LEADS.rowReopenButton(lead.id)} onClick={() => onReopen(lead.id)} className="h-7 text-xs">Reopen</Button>
                  )}
                </td>
              </motion.tr>
            ))}
          </AnimatePresence>
        </tbody>
      </table>
    </div>
  );
}
